import React, { useState, useEffect } from 'react';
import { Tabs, Tab } from 'react-bootstrap';
import { FaPlus, FaEdit, FaTrash, FaCogs, FaTimes, FaSpinner } from 'react-icons/fa';
import { parametriaApi } from '../../services/api/parametriaApi';

import EstadosEmpleadoModal from './modals/EstadosEmpleadoModal';
import SistemaPrivadoPensionesModal from './modals/SistemaPrivadoPensionesModal';
import ParametriaPlanillaModal from './modals/ParametriaPlanillaModal';

// ── Toast ─────────────────────────────────────────────────────────────────────
const Toast = ({ msg, type, onClose }) => {
    useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
    const bg = { success: '#11998e', error: '#ef4444', warning: '#f59e0b' }[type] || '#667eea';
    return (
        <div style={{
            position: 'fixed', top: 20, right: 20, zIndex: 9999,
            background: bg, color: '#fff', padding: '12px 20px', borderRadius: 10,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)', fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: 10, maxWidth: 380, fontFamily: "'Inter', sans-serif"
        }}>
            <span style={{ flex: 1 }}>{msg}</span>
            <FaTimes style={{ cursor: 'pointer', opacity: 0.8 }} onClick={onClose} />
        </div>
    );
};

// ── Modal Confirmar Eliminación ───────────────────────────────────────────────
const ConfirmModal = ({ show, title, itemName, onConfirm, onCancel, loading }) => {
    if (!show) return null;
    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 2100, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Inter', sans-serif"
        }}>
            <div style={{
                background: '#fff', borderRadius: 14, padding: 32, maxWidth: 420, width: '90%',
                boxShadow: '0 16px 48px rgba(0,0,0,0.25)', textAlign: 'center'
            }}>
                <div style={{
                    width: 60, height: 60, borderRadius: '50%', background: '#fef2f2',
                    margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                    <FaTrash style={{ color: '#ef4444', fontSize: 24 }} />
                </div>
                <h5 style={{ fontWeight: 700, marginBottom: 8 }}>{title}</h5>
                <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 24 }}>
                    Se desactivará el registro <strong>{itemName}</strong>.
                    Esta acción no se puede deshacer.
                </p>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                    <button onClick={onCancel}
                        style={{
                            padding: '10px 22px', borderRadius: 8, border: '1.5px solid #d1d5db',
                            background: '#fff', fontWeight: 600, cursor: 'pointer'
                        }}>
                        Cancelar
                    </button>
                    <button onClick={onConfirm} disabled={loading}
                        style={{
                            padding: '10px 22px', borderRadius: 8, border: 'none',
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            color: '#fff', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
                            display: 'flex', alignItems: 'center', gap: 8, opacity: loading ? 0.8 : 1
                        }}>
                        {loading ? <FaSpinner className="fa-spin" /> : <FaTrash />}
                        Desactivar
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
const ParametriaPlanilla = () => {
    const [key, setKey] = useState('estados');
    
    // Data states
    const [estados, setEstados] = useState([]);
    const [afps, setAfps] = useState([]);
    const [parametros, setParametros] = useState([]);
    
    // Modal states
    const [showEstadoModal, setShowEstadoModal] = useState(false);
    const [showAfpModal, setShowAfpModal] = useState(false);
    const [showParamModal, setShowParamModal] = useState(false);
    
    // Selected items for edit
    const [selectedEstado, setSelectedEstado] = useState(null);
    const [selectedAfp, setSelectedAfp] = useState(null);
    const [selectedParam, setSelectedParam] = useState(null);

    // Toast & Confirm Delete states
    const [toast, setToast] = useState(null);
    const [showConfirm, setShowConfirm] = useState(false);
    const [toDelete, setToDelete] = useState(null); // { type, item }
    const [deleting, setDeleting] = useState(false);

    const showToastMsg = (msg, type = 'success') => setToast({ msg, type });

    useEffect(() => {
        if (key === 'estados') fetchEstados();
        else if (key === 'afps') fetchAfps();
        else if (key === 'parametros') fetchParametros();
    }, [key]);

    // Fetch methods
    const fetchEstados = async () => {
        try {
            const response = await parametriaApi.getEstadosEmpleado();
            setEstados(response.data);
        } catch (error) {
            showToastMsg('Error al cargar los estados', 'error');
        }
    };

    const fetchAfps = async () => {
        try {
            const response = await parametriaApi.getSistemasPensiones();
            setAfps(response.data);
        } catch (error) {
            showToastMsg('Error al cargar las AFPs', 'error');
        }
    };

    const fetchParametros = async () => {
        try {
            const response = await parametriaApi.getParametriasPlanilla();
            setParametros(response.data);
        } catch (error) {
            showToastMsg('Error al cargar los parámetros', 'error');
        }
    };

    // Delete handling
    const executeDelete = async () => {
        if (!toDelete) return;
        setDeleting(true);
        try {
            if (toDelete.type === 'estado') {
                await parametriaApi.deleteEstadoEmpleado(toDelete.item.id);
                fetchEstados();
            } else if (toDelete.type === 'afp') {
                await parametriaApi.deleteSistemaPensiones(toDelete.item.id);
                fetchAfps();
            } else if (toDelete.type === 'parametro') {
                await parametriaApi.deleteParametriaPlanilla(toDelete.item.id);
                fetchParametros();
            }
            showToastMsg('Registro desactivado correctamente');
            setShowConfirm(false);
            setToDelete(null);
        } catch (error) {
            showToastMsg(error.response?.data?.detail || 'Error al desactivar el registro', 'error');
        } finally {
            setDeleting(false);
        }
    };

    const requestDeleteEstado = (item) => { setToDelete({ type: 'estado', item }); setShowConfirm(true); };
    const requestDeleteAfp = (item) => { setToDelete({ type: 'afp', item }); setShowConfirm(true); };
    const requestDeleteParam = (item) => { setToDelete({ type: 'parametro', item }); setShowConfirm(true); };

    // Common onSuccess for Modals
    const handleSaved = (actionType, entityType) => {
        showToastMsg(`Registro ${actionType} correctamente`);
        if (entityType === 'estado') fetchEstados();
        else if (entityType === 'afp') fetchAfps();
        else if (entityType === 'parametro') fetchParametros();
    };

    // UI Renderers
    const renderBadge = (activo) => (
        <span style={{
            background: activo === 1 ? '#d1fae5' : '#fee2e2',
            color:      activo === 1 ? '#065f46' : '#991b1b',
            borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700,
            display: 'inline-block'
        }}>
            {activo === 1 ? 'Activo' : 'Inactivo'}
        </span>
    );

    const renderActionButtons = (item, onEdit, onDeleteRequest) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
                onClick={() => onEdit(item)}
                style={{
                    background: 'linear-gradient(135deg,#667eea,#764ba2)',
                    color: '#fff', border: 'none', borderRadius: 7,
                    padding: '6px 12px', cursor: 'pointer', fontSize: 13,
                    display: 'flex', alignItems: 'center', gap: 5
                }}
            >
                <FaEdit /> Editar
            </button>
            {item.activo === 1 && (
                <button
                    onClick={() => onDeleteRequest(item)}
                    title="Desactivar"
                    style={{
                        background: 'linear-gradient(135deg,#ef4444,#dc2626)',
                        color: '#fff', border: 'none', borderRadius: 7,
                        padding: '6px 12px', cursor: 'pointer', fontSize: 13,
                        display: 'flex', alignItems: 'center', gap: 5
                    }}
                >
                    <FaTrash />
                </button>
            )}
        </div>
    );

    const thStyle = { padding: '12px 16px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap', fontSize: 13 };
    const thCenterStyle = { ...thStyle, textAlign: 'center' };
    const tdStyle = { padding: '11px 16px', color: '#374151', fontSize: 14 };
    const tdCenterStyle = { ...tdStyle, textAlign: 'center' };

    let confirmTitle = '';
    let confirmItemName = '';
    if (toDelete) {
        if (toDelete.type === 'estado') { confirmTitle = '¿Desactivar Estado?'; confirmItemName = toDelete.item.nombre; }
        else if (toDelete.type === 'afp') { confirmTitle = '¿Desactivar AFP?'; confirmItemName = toDelete.item.nombre; }
        else if (toDelete.type === 'parametro') { confirmTitle = '¿Desactivar Parámetro?'; confirmItemName = toDelete.item.codigo; }
    }

    return (
        <>
            {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
            
            <ConfirmModal 
                show={showConfirm} 
                title={confirmTitle} 
                itemName={confirmItemName} 
                onConfirm={executeDelete} 
                onCancel={() => { setShowConfirm(false); setToDelete(null); }} 
                loading={deleting} 
            />

            <style>{`
                .dh-carga-bg { background: linear-gradient(135deg,#f8f9fa,#e9ecef); min-height:100vh; padding: 2rem; font-family: 'Inter', sans-serif; }
                .dh-card-main { border:none; border-radius:20px; box-shadow:0 15px 35px rgba(0,0,0,.1); background:#fff; overflow:hidden; }
                .dh-card-header { background:linear-gradient(135deg,#667eea,#764ba2); color:#fff; padding:1.5rem 2rem; border:none; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:12px; }
                .dh-header-title { color:#fff; font-weight:700; font-size:1.5rem; margin:0; display:flex; align-items:center; gap:12px; }
                .btn-dh-primary { background:linear-gradient(135deg,#11998e,#38ef7d); border:none; color:#fff; border-radius:12px; padding:10px 16px; font-weight:600; box-shadow:0 4px 14px rgba(17,153,142,.3); transition:all .3s; cursor:pointer; display:flex; align-items:center; gap:8px; text-decoration:none; font-family: 'Inter', sans-serif; font-size: 14px; }
                .btn-dh-primary:hover { background:linear-gradient(135deg,#38ef7d,#2dd4bf); transform:translateY(-2px); color:#fff; }
                .nav-tabs .nav-link { font-weight: 600; color: #495057; border: none; padding: 12px 20px; margin-right: 5px; border-radius: 10px 10px 0 0; transition: all 0.3s; font-family: 'Inter', sans-serif; }
                .nav-tabs .nav-link:hover { background-color: #f8f9fa; color: #667eea; }
                .nav-tabs .nav-link.active { color: #667eea; background-color: #fff; border-bottom: 3px solid #667eea; }
            `}</style>
            
            <div className="dh-carga-bg">
                <div className="dh-card-main">
                    <div className="dh-card-header">
                        <h5 className="dh-header-title">
                            <FaCogs size={28} />
                            Parametría de Planilla
                        </h5>
                    </div>
                    <div className="p-4" style={{ position: 'relative' }}>
                        <div style={{ position: 'absolute', top: 24, right: 24, zIndex: 10 }}>
                            {key === 'estados' && (
                                <button className="btn-dh-primary" onClick={() => { setSelectedEstado(null); setShowEstadoModal(true); }}>
                                    <FaPlus className="me-2" /> Nuevo Estado
                                </button>
                            )}
                            {key === 'afps' && (
                                <button className="btn-dh-primary" onClick={() => { setSelectedAfp(null); setShowAfpModal(true); }}>
                                    <FaPlus className="me-2" /> Nueva AFP
                                </button>
                            )}
                            {key === 'parametros' && (
                                <button className="btn-dh-primary" onClick={() => { setSelectedParam(null); setShowParamModal(true); }}>
                                    <FaPlus className="me-2" /> Nuevo Parámetro
                                </button>
                            )}
                        </div>
                        <Tabs
                            id="parametria-tabs"
                            activeKey={key}
                            onSelect={(k) => setKey(k)}
                            className="mb-4 border-bottom-0"
                        >
                            <Tab eventKey="estados" title="Estados de Empleado">
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, fontFamily: "'Inter', sans-serif" }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                <th style={thStyle}>Nombre</th>
                                                <th style={thStyle}>Descripción</th>
                                                <th style={thCenterStyle}>Estado</th>
                                                <th style={thCenterStyle}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {estados.map((item, idx) => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? '#fff' : '#fafafa', transition: 'background .15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'} onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}>
                                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{item.nombre}</td>
                                                    <td style={tdStyle}>{item.descripcion}</td>
                                                    <td style={tdCenterStyle}>{renderBadge(item.activo)}</td>
                                                    <td style={tdCenterStyle}>
                                                        {renderActionButtons(item, 
                                                            (data) => { setSelectedEstado(data); setShowEstadoModal(true); },
                                                            requestDeleteEstado
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {estados.length === 0 && (
                                                <tr><td colSpan="4" style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>No hay registros disponibles.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Tab>
                            
                            <Tab eventKey="afps" title="Sistema Privado Pensiones">
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, fontFamily: "'Inter', sans-serif" }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                <th style={thStyle}>Código</th>
                                                <th style={thStyle}>Nombre</th>
                                                <th style={thCenterStyle}>% Aporte</th>
                                                <th style={thCenterStyle}>% Prima Flujo</th>
                                                <th style={thCenterStyle}>% Prima Mixta</th>
                                                <th style={thCenterStyle}>Estado</th>
                                                <th style={thCenterStyle}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {afps.map((item, idx) => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? '#fff' : '#fafafa', transition: 'background .15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'} onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}>
                                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{item.codigo}</td>
                                                    <td style={tdStyle}>{item.nombre}</td>
                                                    <td style={tdCenterStyle}>{item.porcentaje_aporte}%</td>
                                                    <td style={tdCenterStyle}>{item.porc_prima_flujo}%</td>
                                                    <td style={tdCenterStyle}>{item.porc_prima_mixta}%</td>
                                                    <td style={tdCenterStyle}>{renderBadge(item.activo)}</td>
                                                    <td style={tdCenterStyle}>
                                                        {renderActionButtons(item, 
                                                            (data) => { setSelectedAfp(data); setShowAfpModal(true); },
                                                            requestDeleteAfp
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {afps.length === 0 && (
                                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>No hay registros disponibles.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Tab>

                            <Tab eventKey="parametros" title="Parámetros Planilla">
                                <div style={{ overflowX: 'auto' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, fontFamily: "'Inter', sans-serif" }}>
                                        <thead>
                                            <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                                                <th style={thStyle}>Código</th>
                                                <th style={thStyle}>Descripción</th>
                                                <th style={thCenterStyle}>Valor</th>
                                                <th style={thCenterStyle}>Estado</th>
                                                <th style={thCenterStyle}>Acciones</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {parametros.map((item, idx) => (
                                                <tr key={item.id} style={{ borderBottom: '1px solid #f3f4f6', background: idx % 2 === 0 ? '#fff' : '#fafafa', transition: 'background .15s' }} onMouseEnter={e => e.currentTarget.style.background = '#f0f4ff'} onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa'}>
                                                    <td style={{ ...tdStyle, fontWeight: 600, color: '#111827' }}>{item.codigo}</td>
                                                    <td style={tdStyle}>{item.descripcion}</td>
                                                    <td style={tdCenterStyle}>{item.valor}</td>
                                                    <td style={tdCenterStyle}>{renderBadge(item.activo)}</td>
                                                    <td style={tdCenterStyle}>
                                                        {renderActionButtons(item, 
                                                            (data) => { setSelectedParam(data); setShowParamModal(true); },
                                                            requestDeleteParam
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                            {parametros.length === 0 && (
                                                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 48, color: '#9ca3af' }}>No hay registros disponibles.</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </Tab>
                        </Tabs>
                    </div>
                </div>
            </div>

            {/* Modals */}
            <EstadosEmpleadoModal 
                show={showEstadoModal} 
                handleClose={() => setShowEstadoModal(false)} 
                data={selectedEstado}
                onSuccess={(action) => handleSaved(action, 'estado')}
            />
            
            <SistemaPrivadoPensionesModal 
                show={showAfpModal} 
                handleClose={() => setShowAfpModal(false)} 
                data={selectedAfp}
                onSuccess={(action) => handleSaved(action, 'afp')}
            />

            <ParametriaPlanillaModal 
                show={showParamModal} 
                handleClose={() => setShowParamModal(false)} 
                data={selectedParam}
                onSuccess={(action) => handleSaved(action, 'parametro')}
            />
        </>
    );
};

export default ParametriaPlanilla;
